import { policzUtwory, zapiszUtworyRazem, type Utwor } from './db.ts';

// Zasiew korpusu wbudowanego w paczkę. Jedyne miejsce w całej warstwie
// trwałości, które sięga po `import.meta.glob`: `db.ts` nie ma wiedzieć nic o
// paczce ani o kompilatorze, a `main.ts` czyta listę utworów wyłącznie z
// bazy, poza jednym wyjątkiem - trybem awaryjnym bez IndexedDB, gdzie sięga
// po ten sam stały `KORPUS_WBUDOWANY`, bo innego źródła po prostu nie ma.

// Ścieżka jest liczona względem TEGO pliku (`src/store/`), więc korpus leży
// dwa poziomy wyżej. Pierwsza wersja miała tu `../tests/corpus/*.txt`, czyli
// wzorzec celujący w nieistniejące `src/tests/corpus/`, a `import.meta.glob`
// bez ani jednego trafienia NIE zgłasza błędu, tylko zwraca pustą mapę.
// Aplikacja wstawała wtedy poprawnie z pustą listą utworów i żaden wpis w
// konsoli o tym nie mówił. Stąd asercja niżej: pusty korpus jest błędem
// budowania, a nie stanem, w którym wolno wystartować.
const rawCorpus = import.meta.glob('../../tests/corpus/*.txt', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

export interface UtworWbudowany {
  id: string;
  tytul: string;
  wykonawca: string | null;
  rawTab: string;
}

/** Tytuł i wykonawca utworów wbudowanych, tak jak pokazuje je lista. Źródła,
 * nie domysł: wykonawcy z `tests/corpus/README.md` (polska pisownia) i
 * `tests/corpus/golden.ts`, tytuły z tekstu tabów (`Song: Nie Płacz Ewka`,
 * „Wehikuł czasu”, „Bieszczadzkie anioły”, „Jolka, Jolka pamiętasz” stoją w
 * plikach) i z planu projektu („Shape of My Heart”). Utwór spoza tej mapy
 * dostaje tytuł z nazwy pliku, jak dotąd. */
const METADANE_WBUDOWANYCH: Readonly<Record<string, { tytul: string; wykonawca: string }>> = {
  'bieszczadzkie-anioly': { tytul: 'Bieszczadzkie anioły', wykonawca: 'Stare Dobre Małżeństwo' },
  'czarny-blues-o-czwartej-nad-ranem': { tytul: 'Czarny blues o czwartej nad ranem', wykonawca: 'Stare Dobre Małżeństwo' },
  'jolka-jolka-pamietasz': { tytul: 'Jolka, Jolka pamiętasz', wykonawca: 'Budka Suflera' },
  kiler: { tytul: 'Kiler', wykonawca: 'Elektryczne Gitary' },
  'nie-placz-ewka': { tytul: 'Nie płacz Ewka', wykonawca: 'Perfect' },
  niepokonani: { tytul: 'Niepokonani', wykonawca: 'Perfect' },
  perfect: { tytul: 'Perfect', wykonawca: 'Ed Sheeran' },
  'shape-of-my-heart': { tytul: 'Shape of My Heart', wykonawca: 'Sting' },
  'wehikul-czasu': { tytul: 'Wehikuł czasu', wykonawca: 'Dżem' },
};

/** Tytuł, jaki dawniej wynikał z samej nazwy pliku. Po nim poznajemy, że
 * człowiek tytułu nie zmieniał, więc wolno go poprawić. */
function tytulZNazwyPliku(nazwaPliku: string): string {
  return nazwaPliku.replace(/-/g, ' ');
}

/** Identyfikator wyprowadzony z nazwy pliku, nie losowy: ma być stabilny
 * między uruchomieniami, żeby dwa zasiewy (np. po wyczyszczeniu bazy przez
 * przeglądarkę) nigdy nie dały dwóch wpisów tego samego utworu. */
function idZNazwyPliku(nazwaPliku: string): string {
  return `wbudowany:${nazwaPliku}`;
}

// Tytuł bierze się z nazwy pliku bez rozszerzenia, z myślnikami zamienionymi
// na spacje, DOKŁADNIE tak jak w pierwotnej wersji `main.ts`: etykieta na
// ekranie ma pochodzić ze źródła, a nie z domysłu.
export const KORPUS_WBUDOWANY: readonly UtworWbudowany[] = Object.entries(rawCorpus)
  .map(([sciezka, rawTab]) => {
    const nazwaPliku = (sciezka.split('/').pop() as string).replace(/\.txt$/, '');
    const meta = METADANE_WBUDOWANYCH[nazwaPliku];
    return {
      id: idZNazwyPliku(nazwaPliku),
      tytul: meta?.tytul ?? tytulZNazwyPliku(nazwaPliku),
      wykonawca: meta?.wykonawca ?? null,
      rawTab,
    };
  })
  .sort((a, b) => a.tytul.localeCompare(b.tytul, 'pl'));

if (KORPUS_WBUDOWANY.length === 0) {
  throw new Error(
    'Korpus wbudowany jest pusty: wzorzec `import.meta.glob` w `src/store/zasiew.ts` '
    + 'nie trafił w żaden plik. Cicha pustka tutaj daje aplikację bez ani jednego utworu.',
  );
}

/** Wpisuje korpus wbudowany do bazy, WYŁĄCZNIE gdy jest ona pusta. Kolejne
 * uruchomienia mają zastać bazę niepustą i nic nie zrobić: dosiewanie albo
 * nadpisywanie przy każdym starcie skasowałoby zmiany, jakie człowiek
 * poczynił w tytułach czy treści utworów wbudowanych. */
export async function zasiejJesliPusto(): Promise<void> {
  if ((await policzUtwory()) > 0) return;

  // Jedna transakcja na cały korpus. Zapis po jednym utworze zostawiał po
  // przerwanym pierwszym uruchomieniu (zamknięta karta, przeładowanie) bazę
  // z trzema utworami z dziewięciu, a warunek „baza niepusta" wyżej już jej
  // nie dosiewał: niepełny śpiewnik na stałe. Wyszło w teście e2e 25.09.2026.
  const teraz = new Date().toISOString();
  await zapiszUtworyRazem(KORPUS_WBUDOWANY.map((utwor): Utwor => ({
    id: utwor.id,
    tytul: utwor.tytul,
    wykonawca: utwor.wykonawca,
    zrodlo: 'wbudowany',
    rawTab: utwor.rawTab,
    dodanyO: teraz,
    zmienionyO: null,
  })));
}

/**
 * Poprawki tytułów i wykonawców dla śpiewnika zasianego przed 15.10.2026,
 * kiedy tytuł brał się z nazwy pliku („nie placz ewka”) i wykonawcy nie było.
 * Zasiew działa tylko na pustej bazie, więc bez tego istniejące śpiewniki
 * zostałyby przy starych nazwach na zawsze. Rusza wyłącznie to, czego człowiek
 * nie zmienił: tytuł równy dawnemu z nazwy pliku, pusty wykonawca. Zwraca
 * tylko utwory, które naprawdę się zmieniły.
 */
export function poprawkiMetadanych(utwory: readonly Utwor[]): Utwor[] {
  const poprawione: Utwor[] = [];
  for (const utwor of utwory) {
    if (utwor.zrodlo !== 'wbudowany' || !utwor.id.startsWith('wbudowany:')) continue;
    const nazwaPliku = utwor.id.slice('wbudowany:'.length);
    const meta = METADANE_WBUDOWANYCH[nazwaPliku];
    if (!meta) continue;
    const tytul = utwor.tytul === tytulZNazwyPliku(nazwaPliku) ? meta.tytul : utwor.tytul;
    const wykonawca = utwor.wykonawca ?? meta.wykonawca;
    if (tytul !== utwor.tytul || wykonawca !== utwor.wykonawca) {
      poprawione.push({ ...utwor, tytul, wykonawca });
    }
  }
  return poprawione;
}
